import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { DateTime } from 'luxon';
import { ShiftContextService } from 'projects/warehousing/src/app/services/shift-context.service';
import { Subscription } from 'rxjs';
import { Message } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { PieceRateService } from 'projects/warehousing/src/app/services/piece-rate.service';
import { WeeklyTotals } from '@shared/models/weekly-totals';
import { PieceRateActivity } from '@shared/models/piece-rate-activity';
import { PieceRateAdjustment } from '@shared/models/piece-rate-adjustment';
import { Site } from '@shared/models/site';
import { getLabel } from '@shared/helpers/enum-helpers';

@Component({
    selector: 'app-piece-rate-week-status',
    templateUrl: './piece-rate-week-status.component.html',
    styleUrls: ['./piece-rate-week-status.component.scss']
})
export class PieceRateWeekStatusComponent implements OnInit, OnDestroy {
    @Input() set activity(act: PieceRateActivity) {
        this._activity = act;
    }

    saturdayDate: DateTime;
    currentDate: DateTime;
    siteId: string;
    subscriptions: Subscription[];
    _activity: PieceRateActivity;
    adjustment: PieceRateAdjustment;
    weekIsClosed: boolean = false;
    weekIsLocked: boolean = false;
    loadingIndicator: boolean = false;
    loadingIndicatorWT: boolean = false;
    _siteWeekClosedOpenedMsg: Message;
    _siteWeekNotClosedOpenedMsg: Message;

    warningsVisible: boolean = false;
    weeklyTotalsVisible: boolean = false;
    warnings: string[] = [];

    weeklyTotalItems: WeeklyTotals[];
    permittedSites: Site[];

    constructor(private messageService: MessageService, private shiftContextService: ShiftContextService,
                private pieceRateService: PieceRateService) { }

    ngOnInit(): void {
        this.subscriptions = [
            this.shiftContextService.ImmediateSelection.subscribe(context => {
                this.siteId = context.siteId;
                this.currentDate = context.date;
                this.saturdayDate = this.pieceRateService.getWeekEnding(context.date);
            }),
            this.shiftContextService.PermittedSites.subscribe(sites => {
                this.permittedSites = sites;
            }),
            this.pieceRateService.CurrentPieceRateAdjustment.subscribe(buildersPieceRateAdjustment => {
                this.adjustment = buildersPieceRateAdjustment;
                if (buildersPieceRateAdjustment) {
                    this.weekIsClosed = buildersPieceRateAdjustment.weekIsClosed;
                    this.weekIsLocked = buildersPieceRateAdjustment.weekIsLocked;
                }
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    checkWeekStatus(): void {
        if (this.weekIsClosed) {
            this.toggleSiteWeekClosed();
            return;
        }

        this.loadingIndicator = true;

        this.pieceRateService.GetWeekStatus(this.siteId, this.saturdayDate).subscribe({
            next: warnings => {
                this.loadingIndicator = false;

                if (!warnings || !warnings.length)
                    this.toggleSiteWeekClosed();
                else {
                    this.warnings = warnings;
                    this.warningsVisible = true;
                }
            },
            error: _err => {
                this.loadingIndicator = false;
            }
        });
    }

    cancelCloseWeekDialog(): void {
        this.warningsVisible = false;
        this.warnings = [];
    }

    toggleSiteWeekClosed(): void {
        this.loadingIndicator = true;
        let status = 'Opened';
        let severityClass = 'success';
        this.pieceRateService.postSiteWeekClosed(this.siteId, this.saturdayDate, !this.weekIsClosed).subscribe(() => {
            if (this.weekIsClosed) {
                status = 'Closed';
                severityClass = 'warn';
            }

            this._siteWeekClosedOpenedMsg = {
                severity: severityClass,
                summary: 'Piece Rate Week has been ' + status,
                detail: ''
            };
            this.messageService.add(this._siteWeekClosedOpenedMsg);
            this.loadingIndicator = false;
            this.warningsVisible = false;
            this.warnings = [];
        }, _error => {
            if (!this.weekIsClosed)
                status = 'Closed';

            this._siteWeekNotClosedOpenedMsg = {
                severity: 'error',
                summary: 'Piece Rate Week could not be ' + status,
                detail: ''
            };
            this.messageService.add(this._siteWeekNotClosedOpenedMsg);
            this.loadingIndicator = false;
        });
    }

    checkWeeklyTotals(): void {
        this.loadingIndicatorWT = true;

        this.pieceRateService.CheckWeeklyTotals(this.saturdayDate, this._activity).subscribe({
            next: weeklyTotals => {
                this.loadingIndicatorWT = false;
                this.weeklyTotalItems = weeklyTotals.map(wt => ({
                    siteDisplayName: this.permittedSites.find(c => c.id === wt.siteId).displayLabel,
                    enteredBuilderRepairs: wt.enteredBuilderRepairs,
                    inventoryRepairs: wt.inventoryRepairs,
                    totalAdjustment: wt.totalAdjustment
                }));
                this.weeklyTotalsVisible = true;
            },
            error: _err => {
                this.loadingIndicatorWT = false;
            }
        });
    }

    closeWeeklyTotalsDialog(): void {
        this.weeklyTotalsVisible = false;
    }
}
